import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface Member {
  _id: string;
  name: string;
  avatar: string;
  title: string;
  memberType: string;
  facultyRole?: string;
  email: string;
  enrollmentYear?: string;
}

const Members: React.FC = () => {
  const navigate = useNavigate();
  const [, setStudents] = useState<Member[]>([]);
  const [, setFaculty] = useState<Member[]>([]);
  const [labDirectors, setLabDirectors] = useState<Member[]>([]);
  const [groupTeachers, setGroupTeachers] = useState<Member[]>([]);
  const [postdocs, setPostdocs] = useState<Member[]>([]);
  const [alumni, setAlumni] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  
  // 新增：分离博士生和硕士生
  const [phdStudents, setPhdStudents] = useState<Member[]>([]);
  const [masterStudents, setMasterStudents] = useState<Member[]>([]);

  useEffect(() => {
    Promise.all([
      apiService.get<Member[]>('/members/students'),
      apiService.get<Member[]>('/members/faculty'),
      apiService.get<Member[]>('/members/postdocs'),
      apiService.get<Member[]>('/members/alumni')
    ]).then(([studentsRes, facultyRes, postdocsRes, alumniRes]) => {
      // 确保数据存在且为数组
      const studentsData = Array.isArray(studentsRes.data) ? studentsRes.data : [];
      const facultyData = Array.isArray(facultyRes.data) ? facultyRes.data : [];
      const postdocsData = Array.isArray(postdocsRes.data) ? postdocsRes.data : [];
      const alumniData = Array.isArray(alumniRes.data) ? alumniRes.data : [];
      
      // 过滤掉“未知”成员
      const filterUnknown = (m: Member) => m && m._id && m.name && m.title !== '未知' && m.memberType !== '未知';
      const studentsFiltered = studentsData.filter(filterUnknown);
      const facultyFiltered = facultyData.filter(filterUnknown);
      const postdocsFiltered = postdocsData.filter(filterUnknown);
      const alumniFiltered = alumniData.filter(filterUnknown);
      
      console.log('获取到的学生数据:', studentsFiltered);
      console.log('获取到的教师数据:', facultyFiltered);
      
      setStudents(studentsFiltered);
      
      // 将学生分为博士生和硕士生
      const phdStudents = studentsFiltered.filter(member => 
        member && member._id && member.name && (member.title === '博士生' || member.title === '博士')
      );
      const masterStudents = studentsFiltered.filter(member => 
        member && member._id && member.name && (member.title === '硕士生' || member.title === '研究生')
      );
      
      setPhdStudents(phdStudents);
      setMasterStudents(masterStudents);
      
      // 将教师分为实验室负责人和组内教师（排除博士后）
      const directors = facultyFiltered.filter(member => 
        member && member._id && member.name && member.facultyRole === '实验室负责人'
      );
      const teachers = facultyFiltered.filter(member => 
        member && member._id && member.name && 
        (member.facultyRole === '组内教师' || !member.facultyRole) &&
        member.title !== '博士后'
      );
      
      setLabDirectors(directors);
      setGroupTeachers(teachers);
      setFaculty(facultyFiltered); // 保留原有状态以兼容
      
      setPostdocs(postdocsFiltered);
      setAlumni(alumniFiltered);
      setLoading(false);
    }).catch(error => {
      console.error('获取团队成员数据失败:', error);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  const renderMemberCard = (member: Member) => {
    // 确保member对象存在且包含必要属性
    if (!member || !member._id || !member.name) {
      return null;
    }
    
    return (
      <div 
        key={member._id} 
        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => navigate(`/members/${member._id}`)}
      >
        <div className="flex items-center space-x-4">
          <img
            src={member.avatar || '/default-avatar.png'}
            alt={member.name}
            className="w-16 h-16 rounded-full object-cover"
          />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
            <p className="text-gray-600">{member.title || '未设置'}</p>
            {member.enrollmentYear && (
              <p className="text-sm text-gray-500">入学年份: {member.enrollmentYear}</p>
            )}
          </div>
          <div className="text-right">
            <a
              href={`mailto:${member.email || ''}`}
              className="text-blue-600 hover:text-blue-800 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              {member.email || '无邮箱'}
            </a>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">团队成员</h1>
          <p className="text-xl text-gray-600">钙钛矿课题组的研究团队</p>
        </div>

        {/* 教师团队 */}
        {(labDirectors.length > 0 || groupTeachers.length > 0) && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">教</span>
              教师团队
            </h2>
            
            {/* 实验室负责人 */}
            {labDirectors.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">负</span>
                  实验室负责人
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {labDirectors.map(renderMemberCard)}
                </div>
              </div>
            )}
            
            {/* 组内教师 */}
            {groupTeachers.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">师</span>
                  组内教师
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupTeachers.map(renderMemberCard)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 博士后 */}
        {postdocs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">博</span>
              博士后
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {postdocs.map(renderMemberCard)}
            </div>
          </div>
        )}

        {/* 博士生 */}
        {phdStudents.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">博</span>
              博士生
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {phdStudents.map(renderMemberCard)}
            </div>
          </div>
        )}

        {/* 硕士生 */}
        {masterStudents.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">硕</span>
              硕士生
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {masterStudents.map(renderMemberCard)}
            </div>
          </div>
        )}

        {/* 已毕业 */}
        {alumni.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <span className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3">毕</span>
              已毕业
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {alumni.map(renderMemberCard)}
            </div>
          </div>
        )}

        {/* 空状态 */}
        {labDirectors.length === 0 && groupTeachers.length === 0 && postdocs.length === 0 && phdStudents.length === 0 && masterStudents.length === 0 && alumni.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">暂无团队成员信息</h3>
            <p className="text-gray-600">团队成员信息正在更新中...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Members; 